// Global guard against noisy/looping telemetry events.
//
// 1. Filters the benign "ResizeObserver loop ..." browser warnings so they
//    never reach the Lovable error SDK (which would POST them to
//    /__l5e/trackevents and can create a feedback loop).
// 2. Wraps window.__lovableEvents.captureException with a rate limiter:
//    if the same message fires >5 times in 2s, further identical events are
//    dropped for the rest of the session (with exponential backoff-style
//    suppression).

const IGNORED_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
];

function isIgnored(msg: unknown): boolean {
  if (typeof msg !== "string") return false;
  return IGNORED_MESSAGES.some((m) => msg.includes(m));
}

function extractMessage(error: unknown): string {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && "message" in (error as any)) {
    return String((error as any).message ?? "");
  }
  return "";
}

export function installTelemetryGuard() {
  if (typeof window === "undefined") return;
  if ((window as any).__telemetryGuardInstalled) return;
  (window as any).__telemetryGuardInstalled = true;

  // 1) Swallow ResizeObserver warnings before any listener (including the
  //    Lovable SDK) sees them. Use capture=true so we run first.
  const swallow = (e: Event) => {
    const msg =
      (e as ErrorEvent).message ||
      extractMessage((e as PromiseRejectionEvent).reason);
    if (isIgnored(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  };
  window.addEventListener("error", swallow, true);
  window.addEventListener("unhandledrejection", swallow, true);

  // 2) Rate limit captureException on the Lovable SDK, whenever it appears.
  const counters = new Map<string, { count: number; firstAt: number; suppressed: boolean }>();
  const WINDOW_MS = 2000;
  const THRESHOLD = 5;

  const wrap = (orig: (...args: any[]) => any) =>
    function guarded(this: unknown, error: unknown, ...rest: any[]) {
      const msg = extractMessage(error);
      if (isIgnored(msg)) return; // never forward benign browser warnings

      const key = msg.slice(0, 200) || "unknown";
      const now = Date.now();
      const entry = counters.get(key);
      if (!entry) {
        counters.set(key, { count: 1, firstAt: now, suppressed: false });
      } else {
        if (entry.suppressed) return;
        if (now - entry.firstAt > WINDOW_MS) {
          entry.count = 1;
          entry.firstAt = now;
        } else {
          entry.count++;
          if (entry.count > THRESHOLD) {
            entry.suppressed = true;
            return;
          }
        }
      }
      try {
        return orig.call(this, error, ...rest);
      } catch {
        /* never let telemetry throw */
      }
    };

  const patch = () => {
    const evs = (window as any).__lovableEvents;
    if (evs && typeof evs.captureException === "function" && !evs.__guarded) {
      evs.captureException = wrap(evs.captureException.bind(evs));
      evs.__guarded = true;
    }
  };
  patch();
  // The SDK may inject after our script — poll briefly.
  let tries = 0;
  const iv = setInterval(() => {
    patch();
    if (++tries > 40) clearInterval(iv); // ~10s
  }, 250);
}
